import React, { useEffect, useCallback } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import { Typography, Divider, Flex, Button, message, Spin, Form } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import styles from "./ExperienceBooking.module.css"
import { routes } from "router"
import GuestsSelector from "./GuestSelector"
import DateSelector from "./DateSelector"
import DropdownSelector from "./DropDownSelector"
import { fetchExperienceBookingDetails, updateBookingSelections, resetBookingSelections } from "store/experiences"
import { oldRoutes } from "router/old-routes"
import { numberToCurrency } from "helpers/string"
const { Text } = Typography

const BookingDetailCard = ({ bookNow, trip, bookingButtonVisible, activeTab, bookingErrors, setBookingErrors, form, mode }) => {
  const selectedPayment = Form.useWatch("paymentMethod", form)
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const location = useLocation()
  const bookingId = location.state?.b_uuid
  const dispatch = useDispatch()
  const [t] = useTranslation()
  const isBookNow = location?.pathname?.includes("/experience_detail") || location?.pathname?.includes("/trips")
  const {
    experienceById: { experienceDetail },
    experienceBookingData,
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)
  const userRole = useSelector((state) => state.user.role)

  const infoData = [
    { label: "Date", value: "Add Date" },
    { label: "Guests", value: "Add Guests" },
    { label: "Trip Length", value: "Add Trip Length" },
    { label: "Time", value: "Add Time" }
  ]

  const depositAmount = ((bookingSelections?.amount || 0) * (experienceDetail?.deposit_percentage || 0)) / 100
  const balanceAmount = (bookingSelections?.amount || 0) - depositAmount

  const calculateBookingTotal = useCallback(
    (counts, guideFeePercent) => {
      let total = 0
      const breakdown = []

      const { pricing_type, flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger } =
        bookingSelections?.tripLength?.price || {}

      const getPrice = (specificPrice) => Number.parseFloat(specificPrice ?? 0)

      const guestMapping = [
        { key: "adults", label: "Adult", price: price_per_person },
        { key: "children", label: "Child", price: price_per_child },
        { key: "infants", label: "Infant", price: price_per_infant },
        { key: "passengers", label: "Passenger", price: price_per_passenger }
      ]

      const mainCounts = counts?.main || {}
      if (pricing_type === "charge_flat_rate") {
        const flatAmount = Number.parseFloat(flat_rate ?? 0)
        const hasMainGuests = Object.values(mainCounts).some((val) => val > 0)
        if (hasMainGuests) {
          total += flatAmount
          breakdown.push({
            label: "Flat Rate for Main Guests",
            amount: numberToCurrency(flatAmount)
          })
        }
      } else {
        for (const { key, label, price } of guestMapping) {
          const count = mainCounts[key] || 0
          if (count > 0) {
            const unitPrice = getPrice(price)
            const lineTotal = unitPrice * count
            total += lineTotal
            breakdown.push({
              label: `${label} x ${count}`,
              amount: numberToCurrency(lineTotal)
            })
          }
        }
      }

      const extraCounts = counts?.extra || {}
      for (const { key, label, price } of guestMapping) {
        const count = extraCounts[key] || 0
        if (count > 0) {
          const unitPrice = getPrice(price)
          const lineTotal = unitPrice * count
          total += lineTotal
          breakdown.push({
            label: `Extra ${label} x ${count}`,
            amount: numberToCurrency(lineTotal)
          })
        }
      }

      if (guideFeePercent && total > 0) {
        const taxAmount = (total * guideFeePercent) / 100
        total += taxAmount
        console.log(typeof taxAmount)
        breakdown.push({
          label: `Tax & Fees`,
          amount: numberToCurrency(taxAmount)
        })
      }

      return { total, breakdown }
    },
    [bookingSelections?.tripLength?.price]
  )

  useEffect(() => {
    const tripPricing = bookingSelections?.tripLength?.price
    const counts = bookingSelections?.counts
    if (tripPricing && counts) {
      const { total, breakdown } = calculateBookingTotal(counts, trip?.guide_booking_fee_percentage)
      dispatch(updateBookingSelections({ amount: total, breakdown }))
    }
  }, [bookingSelections?.tripLength, bookingSelections?.counts, calculateBookingTotal, dispatch, trip?.guide_booking_fee_percentage])

  const handleDateChange = useCallback(
    (value) => {
      dispatch(updateBookingSelections({ date: dayjs(value).format("YYYY-MM-DD"), timeSlot: null }))
      if (value && bookingSelections.tripLength?.key) {
        dispatch(
          fetchExperienceBookingDetails(
            id,
            "available_slots",
            bookingSelections?.tripLength?.key,
            dayjs(value).format("YYYY-MM-DD"),
            "",
            mode
          )
        )
      }
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, date: false }))
      }
    },
    [dispatch, bookingSelections.tripLength, id, mode, setBookingErrors]
  )

  const handleTripLengthChange = useCallback(
    (tripLength) => {
      dispatch(
        updateBookingSelections({
          tripLength,
          timeSlot: null,
          counts: {
            main: {
              adults: experienceBookingData?.bookingData?.min_guests,
              children: 0,
              infants: 0,
              passengers: 0
            },
            extra: {
              adults: 0,
              children: 0,
              infants: 0,
              passengers: 0
            }
          }
        })
      )
      if (tripLength?.key && bookingSelections.date) {
        dispatch(
          fetchExperienceBookingDetails(
            id,
            "available_slots",
            tripLength?.key,
            dayjs(bookingSelections.date).format("YYYY-MM-DD"),
            "",
            mode
          )
        )
      }
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, tripLength: false }))
      }
    },
    [dispatch, bookingSelections.date, mode, id, setBookingErrors]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      dispatch(updateBookingSelections({ timeSlot: value }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, timeSlot: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      dispatch(updateBookingSelections({ counts: newCounts }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, counts: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  useEffect(() => {
    if (bookNow) {
      if (id) {
        dispatch(fetchExperienceBookingDetails(id, "trip_data", "", "", bookingId, mode))
      }
      // Reset only on Experience Detail or Trips pages, not in the wizard routes
      if (isBookNow) {
        dispatch(resetBookingSelections())
      }
    }
  }, [bookNow, dispatch, id, bookingId, mode, isBookNow])

  return (
    <Spin spinning={experienceBookingData?.loading}>
      <div className={`mt-15 ${styles.bookingCard}`}>
        <Text style={{ fontSize: "18px", fontWeight: 600 }}>{`${bookNow ? trip?.title ?? "" : "From $" + trip?.price_per_person}`}</Text>
        <Flex vertical gap={8} className="py-xl">
          {bookNow === false ? (
            infoData?.map((data, index) => {
              return (
                <Flex justify="space-between" align="center" className={`${styles.cardDetailItems} p-s rounded-s`} key={index + 1}>
                  <Text>{data?.label}</Text>
                  <Text>{data?.value}</Text>
                </Flex>
              )
            })
          ) : (
            <>
              <DateSelector
                value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
                onChange={handleDateChange}
                activeTab={activeTab}
                error={bookingErrors?.date}
                trip={trip}
              />
              <DropdownSelector
                value={bookingSelections.tripLength?.value}
                onChange={handleTripLengthChange}
                label="Trip Length"
                options={
                  experienceBookingData?.bookingData?.trip_durations?.length ? experienceBookingData?.bookingData?.trip_durations : []
                }
                placeholder="Add Trip Length"
                activeTab={activeTab}
                error={bookingErrors?.tripLength}
              />
              <DropdownSelector
                placeholder="Add Time"
                value={bookingSelections?.timeSlot}
                onChange={handleTimeSlotChange}
                label="Time"
                activeTab={activeTab}
                error={bookingErrors?.timeSlot}
                options={experienceBookingData?.bookingData?.data?.length ? experienceBookingData?.bookingData?.data : []}
              />
              <GuestsSelector
                counts={bookingSelections?.counts}
                setCounts={handleGuestsChange}
                activeTab={activeTab}
                error={bookingErrors?.counts}
                setBookingErrors={setBookingErrors}
                maxGuests={experienceBookingData?.bookingData?.max_guests}
                minGuests={experienceBookingData?.bookingData?.min_guests}
              />
            </>
          )}
        </Flex>
        {bookNow && (
          <>
            <Flex vertical gap={4}>
              {bookingSelections?.breakdown?.map((item, index) => (
                <Flex key={index + 1} align="center" justify="space-between">
                  <Text>{item?.label}</Text>
                  <Text>{item?.amount}</Text>
                </Flex>
              ))}
            </Flex>
            <Divider />
            <Flex align="center" justify="space-between">
              <Text strong style={{ fontWeight: 600 }}>
                Total
              </Text>
              <Text style={{ fontWeight: 600 }}>{numberToCurrency(bookingSelections?.amount)}</Text>
            </Flex>
            {selectedPayment && selectedPayment !== "full" && (
              <>
                <Flex align="center" justify="space-between">
                  <Text strong style={{ fontWeight: 600 }}>
                    Pay Now (Deposit)
                  </Text>
                  <Text style={{ fontWeight: 600 }}>{numberToCurrency(depositAmount)}</Text>
                </Flex>
                <Flex align="center" justify="space-between">
                  <Text>
                    {selectedPayment === "later"
                      ? `Balance Due on ${moment(bookingSelections?.date).subtract(1, "day").format("MMMM D, YYYY")}`
                      : `Balance Due on ${moment(bookingSelections?.date).format("MMMM D, YYYY")}`}
                  </Text>
                  <Text>{`$ ${Number.parseFloat(balanceAmount || 0).toFixed(2)}`}</Text>
                </Flex>
              </>
            )}
          </>
        )}
        {bookingButtonVisible && (
          <>
            <Button
              type="primary"
              disabled={!bookNow}
              className={styles.bookButton}
              onClick={() => {
                if (isBookNow) {
                  const { date, counts, tripLength, timeSlot } = bookingSelections

                  const errors = {
                    date: !date,
                    counts: !counts?.main || Object.values(counts?.main).every((v) => Number(v) <= 0),
                    tripLength: !tripLength,
                    timeSlot: !timeSlot
                  }

                  const hasErrors = Object.values(errors).some(Boolean)

                  if (hasErrors) {
                    setBookingErrors(errors)
                    message.error("Please complete all booking selections before proceeding.")
                    return
                  }
                  setBookingErrors({ date: false, guests: false, tripLength: false, timeSlot: false })
                  if (mode) {
                    const isClientUser = userRole === "user"
                    if (isClientUser) navigate(routes.clientBookingWizardRootPath({ id }))
                    else navigate({ pathname: oldRoutes.bookingWizardPath({ id }) })
                  } else {
                    navigate(routes.embeddedExperienceBooking({ uuid, id }))
                  }
                }
              }}
            >
              Book Now
            </Button>
            <Flex justify="center" style={{ marginTop: "10px" }}>
              <Text style={{ fontSize: "12px" }}>{t("webIntegration.payment_info")}</Text>
            </Flex>
          </>
        )}
      </div>
    </Spin>
  )
}

BookingDetailCard.propTypes = {
  bookNow: PropTypes.bool,
  form: PropTypes.object,
  trip: PropTypes.shape({
    min_price: PropTypes.number,
    title: PropTypes.string,
    guide_booking_fee_percentage: PropTypes.number,
    price_per_person: PropTypes.string || PropTypes.number
  }),
  bookingButtonVisible: PropTypes.bool,
  activeTab: PropTypes.number,
  bookingErrors: PropTypes?.object,
  setBookingErrors: PropTypes.func,
  mode: PropTypes.string
}

export default BookingDetailCard
